import { type GetPostedActivitiesQueryVariables, SortOrder } from '@api/mesa/graphql'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { graphql } from '@api/mesa'
import { useGetCardActivityCursor } from './CardActivity'
import { useLogError } from '@/src/common/hooks/useLogError'

graphql(`
  fragment CardActivityFeed on CardActivityFeedResponse {
    page {
      __typename
      ... on CardActivityTransactionEvent {
        transactionEventId
        sourceCreatedAt
        renderedAmount {
          amount
          currencyCode
        }
        merchantDetails {
          description
          name
        }
        pointsEarn {
          points
          pending
          reason
        }
        transactionProcessingType
        transactionEventType: type
      }
      ... on CardActivityCardPayment {
        achTransferId
        sourceCreatedAt
        amount
        transferStatus
      }
      ... on CardActivityStatementCredit {
        statementCreditId
        amount
        name
        statementCreditType: type
        sourceCreatedAt
        highnoteFinancialAccountTransferStatus
      }
      ... on CardActivityFinancialEvent {
        sourceCreatedAt
        amount
        description
        financialEventId
        financialEventType
      }
    }
  }
`)

export function useCardActivityFeed() {
  const getCardActivityCursor = useGetCardActivityCursor()
  const onError = useLogError()

  const {
    data: pendingActivitiesData,
    loading: loadingPendingActivities,
    refetch: refetchPendingActivities,
  } = useLoggedInQuery(
    graphql(`
      query GetPendingActivities {
        cardActivityFeed(
          where: {
            OR: [
              { transactionEventType: { equals: PaymentCardAuthorizationApprovedEvent } }
              { cardPayment: { status: { in: [PENDING, PROCESSING, INITIATED] } } }
              { statementCredit: { highnoteFinancialAccountTransferStatus: { in: [PENDING] } } }
            ]
          }
          take: 88
        ) {
          ...CardActivityFeed
        }
      }
    `),
    { fetchPolicy: 'no-cache', onError },
  )

  const pendingActivities = pendingActivitiesData?.cardActivityFeed.page

  const {
    data: postedActivitiesData,
    loading: loadingPostedActivities,
    fetchMore: fetchMorePostedActivities,
  } = useLoggedInQuery(
    graphql(`
      query GetPostedActivities(
        $cursor: CardActivityFeedCursorInput
        $orderBy: CardActivityOrderByWithRelationInput
      ) {
        cardActivityFeed(
          cursor: $cursor
          orderBy: $orderBy
          where: {
            OR: [
              { transactionEventType: { not: PaymentCardAuthorizationApprovedEvent } }
              { financialEventId: { not: null } }
              { cardPayment: { status: { notIn: [PENDING, PROCESSING, INITIATED] } } }
              { statementCredit: { highnoteFinancialAccountTransferStatus: { in: [SUCCEEDED] } } }
            ]
          }
        ) {
          ...CardActivityFeed
        }
      }
    `),
    { onError },
  )

  const postedActivities = postedActivitiesData?.cardActivityFeed.page

  const fetchMorePostedActivitiesFromCursor =
    (cardActivityIndex: number, sourceCreatedAt: SortOrder) => () => {
      void refetchPendingActivities()

      const postedActivitiesEnd = postedActivities?.at(cardActivityIndex)
      const cursor = postedActivitiesEnd ? getCardActivityCursor(postedActivitiesEnd) : undefined

      void fetchMorePostedActivities({
        variables: {
          cursor,
          orderBy: { sourceCreatedAt },
        } as const satisfies GetPostedActivitiesQueryVariables,
        updateQuery: (previous, { fetchMoreResult }) => ({
          ...fetchMoreResult,
          cardActivityFeed: {
            ...fetchMoreResult.cardActivityFeed,
            page: [...previous.cardActivityFeed.page, ...fetchMoreResult.cardActivityFeed.page],
          },
        }),
      })
    }

  const fetchNewerPostedActivities = fetchMorePostedActivitiesFromCursor(0, SortOrder.Asc)
  const fetchOlderPostedActivities = fetchMorePostedActivitiesFromCursor(-1, SortOrder.Desc)

  return {
    pendingActivities,
    loadingPendingActivities,
    postedActivities,
    loadingPostedActivities,
    fetchNewerPostedActivities,
    fetchOlderPostedActivities,
  } as const
}
